import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clone } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "~hooks/useNotification";
import { useApi } from "~shared/api";
import { getFixtureListQueryKey } from "./useFetchAllSeasonFixtures";

type FixtureProperties = {
  fixtureId: string;
  locked: boolean;
  onSuccess: () => void;
};

export const useLockUnlockFixture = () => {
  const { sport = "", organizationId = "", seasonId = "" } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const notification = useNotification();
  const mutateFunc = async (properties: FixtureProperties) => {
    return await mutateData({
      url: `${sport}/o/${organizationId}/fixtures/${properties.fixtureId}/${properties.locked ? "unlock" : "lock"}`,
      method: "POST",
    });
  };

  const onSuccessHandler = (response: any, properties: FixtureProperties) => {
    if (response?.data && response.data.length > 0 && response.data[0]?.success === true) {
      queryClient.setQueryData(getFixtureListQueryKey({ sport, organizationId, seasonId }), (oldFixtures: []) => {
        return (oldFixtures || []).map((oldFixture: any) => {
          if (oldFixture.fixtureId === properties.fixtureId) {
            const clonedFixture = clone(oldFixture);
            clonedFixture.locked = !clonedFixture.locked;
            return clonedFixture;
          }
          return oldFixture;
        });
      });

      properties.onSuccess?.();

      notification.success({
        message: formatMessage({
          id: properties.locked ? "fixture.unlock.success" : "fixture.lock.success",
        }),
        placement: "bottomRight",
      });
    }
  };

  return useMutation({
    mutationFn: mutateFunc,
    onSuccess: onSuccessHandler,
  });
};
